import dayjs from 'dayjs';
import { TCRMListItem } from 'types/crm';
import { TEvent } from 'types/event';
import { TInterval } from 'types/freeSlots';
import { TReminder } from 'types/reminder';
import { TUser } from 'types/user/user';

import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { TSlotRange, UserEvent, UserReminder } from '../types';

type TUserState = {
    data: TUser;
    crmList: TCRMListItem[];
    events: UserEvent[];
    reminders: UserReminder[];
    freeSlots: TInterval[];
    eventMarkData: { id: number; title: string };
    detailEventId?: number;
    isMyInformationVisible: boolean;
    freeSlotRange: TSlotRange;
    scrollTo?: string;
};

const initialState: TUserState = {
    data: {
        id: 0,
        name: '',
        phone: '',
        surname: '',
        settings: [],
        profile: {} as any,
        worker_positions: [],
        customer_profile: {} as any,
        requisites: [],
        invitation_status_text: '',
        crm_id: [],
    },
    crmList: [],
    events: [],
    reminders: [],
    freeSlots: [],
    eventMarkData: { id: 0, title: '' },
    detailEventId: null,
    isMyInformationVisible: false,
    freeSlotRange: {
        from: dayjs().subtract(1, 'month').toISOString(),
        to: dayjs().add(1, 'month').toISOString(),
    },
    scrollTo: `eventDate${dayjs().format('YYYY-MM-DD')}`,
};

const usersSlice = createSlice({
    name: 'users',
    initialState: initialState,
    reducers: {
        setUserData: (state, action: PayloadAction<any>) => {
            state.data = {
                ...action.payload,
            };
        },
        setUserEvents: (state, action: PayloadAction<TEvent[]>) => {
            const newEvents = action.payload;
            const oldEventsMap = new Map(state.events.map((e) => [e.id, e]));

            state.events = newEvents.map((newEvent) => {
                const oldEvent = oldEventsMap.get(newEvent.id);

                if (!oldEvent) return newEvent;

                return {
                    ...newEvent,
                    isSelected: oldEvent.isSelected ?? false,
                    isMarked: oldEvent.isMarked ?? false,
                };
            });
        },

        addUserEvent: (state, action: PayloadAction<TEvent>) => {
            state.events.push({ ...action.payload, isSelected: false, isMarked: false });
        },

        editUserEvent: (state, action: PayloadAction<TEvent | UserEvent>) => {
            state.events = state.events.map((event) =>
                event.id === action.payload.id ? { ...event, ...action.payload } : event
            );
        },

        setUserReminders: (state, action: PayloadAction<TReminder[]>) => {
            const newReminders = action.payload;
            const oldRemindersMap = new Map(state.reminders.map((r) => [r.id, r]));

            state.reminders = newReminders.map((newReminder) => {
                const oldReminder = oldRemindersMap.get(newReminder.id);

                if (!oldReminder) return { ...newReminder, isSelected: false };

                return {
                    ...newReminder,
                    isSelected: oldReminder.isSelected ?? false,
                };
            });
        },

        addUserReminder: (state, action: PayloadAction<TReminder>) => {
            state.reminders.push({ ...action.payload, isSelected: false });
        },

        editUserReminder: (state, action: PayloadAction<TReminder>) => {
            state.reminders = state.reminders.map((reminder) =>
                reminder.id === action.payload.id ? { ...reminder, ...action.payload } : reminder
            );
        },
        setUserCrmList: (state, action: PayloadAction<TCRMListItem[]>) => {
            state.crmList = action.payload;
        },

        setIsMyInformationVisible: (state, action: PayloadAction<boolean>) => {
            state.isMyInformationVisible = action.payload;
        },
        setEventMarkData: (state, action: PayloadAction<{ id: number; title: string }>) => {
            state.eventMarkData = action.payload;
        },
        selectEvent: (state, action: PayloadAction<{ eventId: number; activePart: number }>) => {
            const { eventId, activePart } = action.payload;
            state.events = state.events.map((event) =>
                event.id === eventId
                    ? {
                          ...event,
                          isSelected: !event.isSelected,
                          activePart: event.isSelected ? 0 : activePart,
                          isMarked: false,
                      }
                    : event
            );
        },
        selectReminder: (
            state,
            action: PayloadAction<{ reminderId: number; activePart: number }>
        ) => {
            const { reminderId, activePart } = action.payload;
            state.reminders = state.reminders.map((reminder) =>
                reminder.id === reminderId
                    ? {
                          ...reminder,
                          isSelected: !reminder.isSelected,
                          activePart: reminder.isSelected ? 0 : activePart,
                      }
                    : reminder
            );
        },
        clearSelection: (state) => {
            state.events = state.events.map((event) => ({ ...event, isSelected: false }));
            state.reminders = state.reminders.map((reminder) => ({
                ...reminder,
                isSelected: false,
            }));
        },
        setDetailEventId: (state, action: PayloadAction<number | null>) => {
            state.detailEventId = action.payload;
        },
        setFreeSlotRange: (state, action: PayloadAction<{ from: string; to: string }>) => {
            state.freeSlotRange = action.payload;
        },
        setScrollTo: (state, action: PayloadAction<string>) => {
            state.scrollTo = action.payload;
        },
        setUserFreeSLots: (state, action: PayloadAction<TInterval[]>) => {
            state.freeSlots = action.payload;
        },
    },
});

export default usersSlice.reducer;

export const {
    setUserData,
    setUserEvents,
    addUserEvent,
    editUserEvent,
    setUserReminders,
    addUserReminder,
    editUserReminder,
    setIsMyInformationVisible,
    setUserCrmList,
    setEventMarkData,
    selectEvent,
    selectReminder,
    clearSelection,
    setDetailEventId,
    setFreeSlotRange,
    setUserFreeSLots,
    setScrollTo,
} = usersSlice.actions;
